import User from '~/model/user';
import { Store } from 'react-notifications-component';
import config from '~/config';

export const sleep = (time: number) => new Promise((resolve) => setTimeout(resolve, time));

export const valueIsDefined = (value: any) => value !== undefined && value !== null;

export const valueIsEmpty = (value: any) => {
  if (typeof value === 'string') {
    value = value.trim();
  }
  // allow 0 and FALSE
  return value !== 0 && value !== false && !value;
}

export const mapNonEmpty = (obj: any) => Object.entries(obj).reduce((a: any, [k, v]) => (!valueIsEmpty(v) ? (a[k] = v, a) : a), {});

export const hasNonEmpty = (obj: any) => Object.keys(mapNonEmpty(obj)).length > 0;

export const isObject = (obj: any) => typeof obj === 'object' && obj !== null;

export const showNotification = (type: string, message: string, options?: any) => {
  const defaultOptions = {
    title: '',
    insert: 'top',
    container: 'top-center',
    animationIn: ['animate__animated', 'animate__fadeIn'],
    animationOut: ['animate__animated', 'animate__fadeOut'],
    dismiss: {
      duration: 6000,
      onScreen: true,
      pauseOnHover: true,
      showIcon: true,
    },
    width: 400,
  }
  return Store.addNotification({
    ...defaultOptions,
    ...options,
    type: type,
    message: message,
  });
}

export const showBriefNotification = (type: string, message: string, options?: any) => {
  const defaultOptions = {
    title: '',
    insert: 'top',
    container: 'top-center',
    animationIn: ['animate__animated', 'animate__fadeIn'],
    animationOut: ['animate__animated', 'animate__fadeOut'],
    dismiss: {
      duration: 2000,
      onScreen: true,
      pauseOnHover: true,
      showIcon: true,
    },
    width: 400,
  }
  return Store.addNotification({
    ...defaultOptions,
    ...options,
    type: type,
    message: message,
  });
}

export const showSuccess = (message: string, options?: any) => showNotification('success', message, options);

export const showBriefSuccess = (message: string, options?: any) => showBriefNotification('success', message, options);

export const showMessage = (message: string, options?: any) => showNotification('info', message, options);

export const showWarning = (message: string, options?: any) => showNotification('warning', message, options);

export const showError = (message: string, options?: any) => showNotification('danger', message, options);

export const showBriefError = (message: string, options?: any) => showBriefNotification('danger', message, options);

export const removeNotification = (id: string) => Store.removeNotification(id);

export const trim = (str: string, char: string) => {
  char = char.replace(/[-/^$*+?.()|[]{}]/g, '$&');
  return str.replace(new RegExp(
    "^[" + char + "]+|[" + char + "]+$", "g"
  ), "");
}

export const ltrim = (s: string, c: string) => {
  c = c.replace(/[-/^$*+?.()|[]{}]/g, '$&');
  return s.replace(new RegExp(
    "^[" + c + "]+", "g"
  ), "");
}

export const rtrim = (s: string, c: string) => {
  c = c.replace(/[-/^$*+?.()|[]{}]/g, '$&');
  return s.replace(new RegExp(
    "[" + c + "]+$", "g"
  ), "");
}

export const buildUrl = (base: string, fragment: string) => rtrim(base, '/') + (fragment ? '/' + ltrim(fragment, '/') : '');

export const getBeUrl = (fragment?: string) => buildUrl(config.API_BE_URL as string, fragment as string);

export const nullsToEmptyStrings = (obj: any) => {
  if (!obj) {
    return;
  }
  if (Array.isArray(obj)) {
    return obj.map(v => v === null ? '' : v);
  } else {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === null ? '' : v]));
  }
}

export const toUserModel = (user: any) => Object.assign(new User(), user);

export const withAuth = (fragment: string) => buildUrl(config.AUTH_PATH_PREFIX as string, fragment);

export const randomString = (len: number) => new Promise((resolve, reject) => {
  try {
    const array = new Uint8Array(len);
    window.crypto.getRandomValues(array);
    const hex = Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    resolve(hex);
  } catch (error) {
    reject(null);
  }
});

export const mergeRefs = (...refs: any[]) => {
  const filteredRefs = refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 0) return filteredRefs[0];
  return (inst: any) => {
    for (const ref of filteredRefs) {
      if (typeof ref === 'function') {
        ref(inst);
      } else if (ref) {
        ref.current = inst;
      }
    }
  };
};

export const dictionaryToSelectOptions = (dict: any) => Object.entries(dict).map(entry => ({ label: entry[1], value: entry[0] }));

export const formatPhone = (phone: string) => {
  if (!phone) {
    return;
  }
  const clean = phone.replace(/\D/g, '');
  const match = clean.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (!match) {
    return clean;
  }
  let result = '';
  if (!!match[1]) {
    result += match[1];
  }
  if (!!match[2]) {
    result += '-' + match[2];
  }
  if (!!match[3]) {
    result += '-' + match[3];
  }
  return result;
}

export const stripTags = (value: string) => value.replace(/(<([^>]+)>)/gi, "");

export const filterObjectKeys = (obj: any, keys: string[]) => {
  if (!obj) {
    return null;
  }
  return Object.fromEntries(Object.entries(obj).filter(([key]) => keys.includes(key)));
}
